import type { SetStateAction } from "react";

import type { DataPoint, PieChartData } from "../utils";
import { tooltipLabelFormat } from "../utils";

/**
 * Transform outside Pie legend,
 * if legend is showed inside the Pie, remove it from outside
 * @param legendLabels Labels that should appear outside the Pie
 */
export const legendLabelsTransform = (
    chart: { data: { datasets: any[]; labels: string | string[] } },
    legendLabels: string[],
) => {
    const dataset = chart.data.datasets[0];

    return legendLabels.map((label, i) => {
        const dataIndex = chart.data.labels.indexOf(label);
        return {
            text: label,
            fillStyle: dataset.backgroundColor[dataIndex],
            strokeStyle: dataset.borderColor ? dataset.borderColor[dataIndex] : dataset.backgroundColor[dataIndex],
            hidden: false,
            index: dataIndex,
            pointStyle: "rectRounded",
            fontColor: "#646464",
        };
    });
};

/**
 * Handle show labels inside the Pie, in the correspunding slice of data
 * As Chart.js render canvas, we can't adjust it with simple CSS
 *
 * I adapted a logic to get an aproximate value of the slice width and it label width
 * Show the label only if the slice width > text width
 *
 * @param value Current slice value
 * @param context Chart.js context data (couldn't type is properly)
 * @param data Data for Pie chart
 * @param setLabelsInChart Save visible labels in chart inside a state
 *
 */
export const insidePieLabelsTransform = (
    value: number,
    context: any,
    data: PieChartData,
    setLabelsInChart: React.Dispatch<SetStateAction<string[]>>,
) => {
    // Get the value percentage on total values
    const totalValues: number = data.datasets[0].data.reduce((sum, val) => sum + val, 0) / 100;
    const valuePercentage = (value / 100 / totalValues) * 100;

    // Get the width of the canvas/slice
    const chartWidth = context.chart.width;
    const sliceWidth = (valuePercentage / 100) * chartWidth;

    // Get data label -->
    const dataIndex = context.dataIndex;
    const label = context.chart.data.labels[dataIndex];

    const textWidth = context.chart.ctx.measureText(label).width / 2;
    // Only show label if there is enought space
    if (sliceWidth > textWidth) {
        setLabelsInChart((prev) => {
            if (!prev.includes(label)) return [...prev, label];
            return prev;
        });
        return label;
    } else {
        setLabelsInChart((prev) => {
            if (prev.includes(label)) return prev.filter((item) => item !== label);
            return prev;
        });
        return "";
    }
};

/**
 * This function is used to customize a Chart.js tooltip.
 * It's similar to the original tooltip and allows to display a larger amount of data.
 * Used in PieChart to display the detail amounts by country when there is data in the "Other" slice.
 * @param context context Chart.js data
 * @param dataType type of data "quantities" or "turnover"
 */
export const externalTooltipHandler = (context, dataType) => {
    let tooltipEl = document.getElementById("chartjs-tooltip");

    // Create tooltip element if there is not
    if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = "chartjs-tooltip";
        tooltipEl.className = "chartjs-tooltip";
        document.body.appendChild(tooltipEl);
    }

    // Hide tooltip (I tried with class but it didn't work, I don't know why)
    const tooltipModel = context.tooltip;
    if (tooltipModel.opacity === 0) {
        tooltipEl.style.opacity = "0";
        setTimeout(() => {
            if (tooltipEl) {
                tooltipEl.remove();
            }
        }, 300);
        return;
    }

    const dataOthers = context.chart.data.datasets[0].dataOthers as DataPoint[];

    // Format Tooltip html
    const dataPoint = tooltipModel.dataPoints[0];
    const { label, labelLines } = tooltipLabelFormat(dataPoint, dataType, dataOthers);

    let innerHtml = `<div class="tooltip-title" style="font-weight: bold; margin-bottom: 4px;">${label}</div>`;
    innerHtml += "<table>";
    labelLines.forEach((line) => {
        innerHtml += `<tr><td>${line}</td></tr>`;
    });
    innerHtml += "</table>";
    tooltipEl.innerHTML = innerHtml;

    // Position the tooltip near the cursor
    const position = context.chart.canvas.getBoundingClientRect();
    tooltipEl.style.opacity = "1";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + "px";
    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + "px";
};
